/**
 * Shared city validation utility.
 * Validates city names via cache → presets → backend /api/validate-city endpoint.
 * Used by WanderAppComplete (from/to) and PersonalizationModal (home city).
 *
 * Auth bridge: call configureValidateCityApi(fn) to inject a platform-specific
 * API caller (e.g., one that adds Bearer tokens). Falls back to plain fetch.
 */

// Auth bridge: platform-specific API caller (injected by web/mobile)
let _callApi = null;

/**
 * Configure a custom API caller for authenticated requests.
 * @param {Function} fn - async (url, options) => Response
 */
export function configureValidateCityApi(fn) {
  _callApi = fn;
}

// Cache for validated city names — in-memory + localStorage for persistence
const validatedCityCache = {};
let _cacheLoaded = false;

// Lazily load city cache from localStorage on first use.
// NOT at module scope — localStorage doesn't exist in Node.js or React Native,
// and ESM module evaluation would trigger it during barrel imports.
function ensureCacheLoaded() {
  if (_cacheLoaded) return;
  _cacheLoaded = true;
  try {
    if (typeof localStorage === 'undefined') return;
    const stored = localStorage.getItem('wander_city_cache');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Only load entries less than 30 days old
      Object.entries(parsed).forEach(([k, v]) => {
        if (v._ts && Date.now() - v._ts < 30 * 24 * 60 * 60 * 1000) {
          validatedCityCache[k] = v;
        }
      });
    }
  } catch (e) { /* ignore — not in browser */ }
}

const persistCityCache = () => {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem('wander_city_cache', JSON.stringify(validatedCityCache));
  } catch (e) { /* ignore */ }
};

// Validate a city name using Haiku — checks in-memory cache, then localStorage, then Supabase-cached backend
// Returns { valid, correctedName } or null on error
export const validateCityName = async (cityInput) => {
  if (!cityInput || !cityInput.trim()) return null;
  ensureCacheLoaded();
  const key = cityInput.trim().toLowerCase();

  // 1. Check in-memory + localStorage cache
  if (validatedCityCache[key]) return validatedCityCache[key];

  // 2. Known valid preset cities — skip API call
  const presets = ['nyc', 'new york', 'los angeles', 'chicago', 'san francisco', 'london', 'paris', 'tokyo', 'sydney', 'miami', 'boston', 'seattle', 'denver', 'austin', 'toronto', 'vancouver', 'montreal'];
  if (presets.includes(key)) {
    const result = { valid: true, correctedName: cityInput.trim(), _ts: Date.now() };
    validatedCityCache[key] = result;
    persistCityCache();
    return result;
  }

  // 3. Call backend endpoint (checks Supabase llm_cache before hitting Haiku)
  try {
    const apiUrl = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:3001"}/api/validate-city`;
    const apiOptions = {
      method: "POST",
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ city: cityInput.trim() })
    };
    const response = await (_callApi || fetch)(apiUrl, apiOptions);

    if (response.ok) {
      const result = await response.json();

      const cached = {
        valid: !!result.valid,
        correctedName: result.valid ? (result.correctedName || cityInput.trim()) : null,
        _ts: Date.now()
      };

      validatedCityCache[key] = cached;
      persistCityCache();
      return cached;
    }
    // Server returned an error (404, 500, etc.)
    if (process.env.NODE_ENV === 'development') console.warn(`City validation endpoint returned ${response.status} — is the backend running with the latest code?`);
    return { valid: false, correctedName: null, error: 'validation_unavailable' };
  } catch (error) {
    // Network error (backend down entirely) — be permissive so the app doesn't break
    if (process.env.NODE_ENV === 'development') console.error('City validation network error:', error);
    return { valid: true, correctedName: cityInput.trim() };
  }
};

export default validateCityName;
